import { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { LABEL, CUSTOM_MESSAGES } from '@veriables';
import { transferCheckToEmp } from '@reducers/storeChecks';
import ModalHeader from '@components/common/ModalHeader';
import { TouchableButton } from '@components/common/Touchable';

const FullWidthModal = lazy(() =>
  import('@components/common/Modal/FullWidthModal')
);

const useEmployeeTransfer = ({
  checkData,
  authInfo,
  serverAppTheme,
  setErrorPopUp,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const employees = authInfo?.employees || [];

  const open = useCallback(() => setIsOpen(true), []);

  const reset = useCallback(() => {
    setIsOpen(false);
    setSelectedEmployee(null);
  }, []);

  const handleTransfer = useCallback(() => {
    if (!selectedEmployee) {
      setErrorPopUp({
        res: 'Please select an employee to transfer',
        noErrorTxt: true,
      });
      return;
    }
    dispatch(
      transferCheckToEmp({
        storeId: authInfo?.id,
        checkHeaderId: checkData?.id,
        employeeId: selectedEmployee.id,
      })
    )
      .then(() => {
        onClose(true);
        reset();
      })
      .catch(err => setErrorPopUp({ res: err?.message }));
  }, [
    selectedEmployee,
    dispatch,
    authInfo,
    checkData,
    onClose,
    reset,
    setErrorPopUp,
  ]);

  const modalStyle = useMemo(
    () => ({
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      pointerEvents: 'auto',
      backgroundColor: serverAppTheme?.popUp_bg,
      backgroundClip: 'padding-box',
      height: '100%',
      borderRadius: '16px',
      border: serverAppTheme?.popupBorder,
      boxShadow: serverAppTheme?.popUp_box_shadow,
    }),
    [serverAppTheme]
  );

  const jsx = isOpen ? (
    <Suspense fallback={null}>
      <FullWidthModal
        isOpen={isOpen}
        isModalCenter
        customClass2="w-100 h-100"
        noBackground
        style={modalStyle}
      >
        <div
          className="col-12 px-0 m-auto h-100 justify-content-center align-items-center position-relative text-center"
          style={{ color: serverAppTheme?.primary_text }}
        >
          <ModalHeader
            stationName={checkData?.stationName}
            tileHeader="Transfer Employee"
            seatNumber={checkData?.number}
          />
          <div className="mt-3 px-2" style={{height: "auto"}}>
            <div className="col-12 px-0 text-center fw-600 fs_16 mb-3">
              {CUSTOM_MESSAGES().CHK_INSTRUCTION_TRANSFER_EMPLOYEE}
            </div>
            <div
              className="row mx-0 flex-wrap overflowY"
              style={{ maxHeight: '71vh' }}
            >
              {employees.map(emp => (
                <div key={emp.id} className="col-3 p-1">
                  <button
                    type="button"
                    className={cn(
                      'w-100 d-flex justify-content-center align-items-center rounded fs_12 btn-ripple'
                    )}
                    style={{
                      minHeight: '14vh',
                      background:
                        emp.id === selectedEmployee?.id
                          ? serverAppTheme?.selected
                          : serverAppTheme?.unselected,
                      border:
                        emp.id === selectedEmployee?.id
                          ? 'none'
                          : serverAppTheme?.popupBorder,
                      color:
                        emp.id === selectedEmployee?.id
                          ? '#000'
                          : serverAppTheme?.primary_text,
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedEmployee(emp)}
                  >
                    {`${emp?.firstName || ''} ${emp?.lastName || ''}`}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div
            className="row mx-0 justify-content-center position-absolute"
            style={{ bottom: '16px', left: 0, width: '100%'}}
          >
            <div className="col-4 px-0 pr-1 d-flex justify-content-start align-items-center">
              <TouchableButton
                type="button"
                className="col-12 px-0 rounded btn-ripple fs_24 fw-600 btn44"
                onClick={reset}
                style={{
                  backgroundColor: serverAppTheme?.OFF_Premise_Btn,
                  border: serverAppTheme?.cancleBtn_Border,
                  color: serverAppTheme?.cancleBtn_font,
                }}
                rippleEffect
              >
                {LABEL().CANCEL}
              </TouchableButton>
            </div>
            <div className="col-4 px-0 pl-1 d-flex justify-content-end align-items-center">
              <TouchableButton
                type="button"
                className="col-12 px-0 rounded btn-ripple fs_24 fw-600 btn44"
                onClick={handleTransfer}
                style={{
                  backgroundColor: serverAppTheme?.secondaryBtn,
                  color: serverAppTheme?.btn_font_color,
                }}
                rippleEffect
              >
                {LABEL().TRANSFER}
              </TouchableButton>
            </div>
          </div>
        </div>
      </FullWidthModal>
    </Suspense>
  ) : null;

  return { open, jsx };
};

export default useEmployeeTransfer;
